import { useCallback, useContext } from 'react'
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router'

interface HistoryLikeNavigator {
  location: { search: string }
}

const isHistoryLikeNavigator = (navigator: object): navigator is HistoryLikeNavigator =>
  'location' in navigator && typeof (navigator as HistoryLikeNavigator).location?.search === 'string'

/**
 * Returns a getter for `location.search` as it is at call time, unlike `useLocation`, which returns a snapshot
 * taken during render. Needed when a hook rewrites one query param and has to keep params written by a
 * neighbouring hook in the same tick. React Router v6 has no hook for this, so the value is read from the
 * navigator: every history-backed router (`HistoryRouter` in the app, `MemoryRouter` in tests) passes its history
 * object there, and that object holds the live location.
 */
export const useLiveSearch = (): (() => string) => {
  const { navigator } = useContext(NavigationContext)

  return useCallback(
    () => (isHistoryLikeNavigator(navigator) ? navigator.location.search : window.location.search),
    [navigator],
  )
}
